import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useAgents } from "@/contexts/AgentContext";
import { useToast } from "@/hooks/use-toast";
import MainLayout from "@/components/layout/MainLayout";
import AgentCreationModal from "@/components/agent/AgentCreationModal";
import ArchivedAgentsSection from "@/components/agent/ArchivedAgentsSection";
import VoiceAgentCreationModal from "@/components/voice/VoiceAgentCreationModal";
import { GradientPageHeader } from "@/components/layout/GradientPageHeader";
import { PerformanceStatsSection } from "@/components/home";
import { analyticsService, AnalyticsData } from "@/services/analyticsService";
import {
  OrganizationErrorAlert,
  VoiceAgentsSection,
  ChatAgentsSection,
  DeleteAgentDialog,
} from "@/components/agents-list";
import CreateAgentDropdown from "@/components/agent/CreateAgentDropdown";

const AgentList = () => {
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateVoiceModalOpen, setIsCreateVoiceModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Delete dialog states
  const [deleteAgentId, setDeleteAgentId] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [selectedAgentName, setSelectedAgentName] = useState("");
  const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [organizationError, setOrganizationError] = useState<string | null>(
    null,
  );

  // Analytics state (moved from Home page)
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    leadsCollected: 0,
    assistedCustomers: 0,
    timeSaved: 0,
    messages: 0,
  });
  const [timeFrame, setTimeFrame] = useState("30d");

  const { user, organization } = useAuth();
  const hideChatAgents = organization?.hide_chat_agents ?? true;
  const {
    activeChatAgents,
    isLoadingChatAgents,
    chatAgentStats,
    activeVoiceAgents,
    isLoadingVoiceAgents,
    voiceAgentStats,
    voiceAgentsEnabled,
    refreshAgents,
  } = useAgents();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch analytics data (moved from Home page)
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!organization?.id) return;

      try {
        const { data, error } = await analyticsService.getAnalytics(
          organization.id,
          timeFrame as "all" | "30d",
        );

        if (error) {
          console.error("Analytics error:", error);
          return;
        }

        setAnalytics(data);
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      }
    };

    fetchAnalytics();
  }, [organization?.id, timeFrame]);

  // Function to get gradient color based on agent ID
  const getAgentGradient = (id: string): string => {
    const colors = [
      "from-blue-100 to-blue-300",
      "from-purple-100 to-purple-300",
      "from-pink-100 to-pink-300",
      "from-green-100 to-green-300",
      "from-amber-100 to-amber-300",
      "from-cyan-100 to-cyan-300",
      "from-indigo-100 to-indigo-300",
      "from-rose-100 to-rose-300",
      "from-emerald-100 to-emerald-300",
      "from-orange-100 to-orange-300",
    ];

    const index =
      id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;
    return `bg-gradient-to-br ${colors[index]}`;
  };

  const handleDelete = async () => {
    if (!deleteAgentId) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/agent/${deleteAgentId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete agent");
      }

      toast({
        title: "Agent deleted",
        description: "The agent has been successfully deleted.",
      });

      setIsDeleteDialogOpen(false);
      refreshAgents();
    } catch (error: any) {
      console.error("Failed to delete agent:", error);
      toast({
        variant: "destructive",
        title: "Failed to delete agent",
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (id: string, name: string) => {
    setDeleteAgentId(id);
    setSelectedAgentName(name);
    setDeleteConfirmText("");
    setIsDeleteButtonDisabled(true);
    setIsDeleteDialogOpen(true);
  };

  const handleSetupOrganization = () => {
    navigate("/home");
  };

  const handleNavigateToDetail = (id: string) => {
    navigate(`/agents/${id}`);
  };

  const handleViewConversations = (e: React.MouseEvent, agentId: string) => {
    e.stopPropagation();
    navigate(`/agents/${agentId}`, { state: { activeTab: "conversations" } });
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const copyAgentLink = async (e: React.MouseEvent, agent: any) => {
    e.stopPropagation();
    const url = `${window.location.origin}/${encodeURIComponent(organization?.agent_url)}/${agent.public_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied",
        description: "Agent link has been copied to clipboard",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: "Unable to access clipboard",
      });
    }
  };

  const openAgentChat = (e: React.MouseEvent, agent: any) => {
    e.stopPropagation();
    window.open(
      `/${encodeURIComponent(organization?.agent_url)}/${agent.public_id}`,
      "_blank",
    );
  };

  const handleDeleteConfirmChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDeleteConfirmText(e.target.value);
    setIsDeleteButtonDisabled(e.target.value.toLowerCase() !== "delete");
  };

  return (
    <MainLayout>
      {/* Fixed GradientPageHeader */}
      <GradientPageHeader
        title="Manage your AI agents"
        titleHighlight="agents"
        description={
          hideChatAgents
            ? "Hubflo agents can engage your leads and clients over the phone"
            : "Hubflo agents can engage your leads and clients via chat or over the phone"
        }
      >
        <CreateAgentDropdown
          voiceAgentsEnabled={voiceAgentsEnabled}
          onCreateChatAgent={() => setIsCreateModalOpen(true)}
          onCreateVoiceAgent={() => setIsCreateVoiceModalOpen(true)}
          disabled={!organization}
          hideChatAgents={hideChatAgents}
        />
      </GradientPageHeader>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-auto">
        <div className="container mx-auto py-6 max-w-[68rem]">
          {/* Performance Stats Section (moved from Home page) - hide when chat agents disabled */}
          {!hideChatAgents && (
            <PerformanceStatsSection
              analytics={analytics}
              timeFrame={timeFrame}
              onTimeFrameChange={setTimeFrame}
            />
          )}

          <OrganizationErrorAlert
            organizationError={organizationError}
            onSetupOrganization={handleSetupOrganization}
          />

          {/* Unified Sections View */}
          <div className="space-y-12">
            <VoiceAgentsSection
              voiceAgentsEnabled={voiceAgentsEnabled}
              onCreateVoiceAgent={() => setIsCreateVoiceModalOpen(true)}
              agents={activeVoiceAgents}
              isLoading={isLoadingVoiceAgents}
              agentStats={voiceAgentStats}
            />

            {!hideChatAgents && (
              <ChatAgentsSection
                agents={activeChatAgents}
                agentStats={chatAgentStats}
                isLoading={isLoadingChatAgents}
                organizationError={organizationError}
                organization={organization}
                onCreateAgent={() => setIsCreateModalOpen(true)}
                onNavigateToDetail={handleNavigateToDetail}
                onViewConversations={handleViewConversations}
                onCopyAgentLink={copyAgentLink}
                onOpenAgentChat={openAgentChat}
                getAgentGradient={getAgentGradient}
                getInitials={getInitials}
              />
            )}

            {/* Archived Agents Section */}
            {!hideChatAgents && !isLoadingChatAgents && !organizationError && (
              <ArchivedAgentsSection onUnarchiveSuccess={refreshAgents} />
            )}
          </div>

          <DeleteAgentDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onDelete={handleDelete}
            selectedAgentName={selectedAgentName}
            deleteConfirmText={deleteConfirmText}
            onDeleteConfirmChange={handleDeleteConfirmChange}
            isDeleteButtonDisabled={isDeleteButtonDisabled}
            isDeleting={isDeleting}
          />

          {/* Agent Creation Modal */}
          <AgentCreationModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          />

          {/* Voice Agent Creation Modal */}
          <VoiceAgentCreationModal
            isOpen={isCreateVoiceModalOpen}
            onClose={() => setIsCreateVoiceModalOpen(false)}
            onSuccess={(agent) => {
              setIsCreateVoiceModalOpen(false);
              // Navigate to the voice agent detail page
              navigate(`/voice-agents/${agent.id}`);
            }}
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default AgentList;
